<template>
  <div class="ma-4 px-4">
    <searchBar
      @add="openCreateDialog"
      @updateSearch="(v) => (search = v)"
      headerText=""
      @fileUploaded="initialise"
    />

    <!-- Status Filter Radio Buttons -->
    <v-row style="margin-bottom: 3px">
      <v-col cols="12">
        <v-radio-group
          v-model="statusFilter"
          row
          dense
          hide-details
          class="mt-0 pt-0"
        >
          <v-radio label="All" value="all" />
          <v-radio label="Active" value="active" />
          <v-radio label="Inactive" value="inactive" />
        </v-radio-group>
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :search="search"
            :items="filteredAddons"
            :custom-filter="customFilter"
            fixed-header
            class="secondary row-height-50"
            :loading="loading"
            loading-text="Loading add-ons..."
          >
            <template #[`item.action`]="{ item }">
              <v-icon small class="mr-2" color="accent" @click="edit(item)">
                mdi-pencil
              </v-icon>
            </template>

            <template #[`item.type`]="{ item }">
              <v-chip
                small
                @click="item.type === 'Combo' ? showAddons(item) : null"
                :color="item.type === 'Combo' ? 'primary' : 'default'"
                :style="item.type === 'Combo' ? 'cursor: pointer' : ''"
              >
                {{ item.type }}
              </v-chip>
            </template>

            <template #[`item.description`]="{ item }">
              <v-btn small text color="primary" @click="showDialog(item)">
                View
              </v-btn>
            </template>

            <template #[`item.name`]="{ item }">
              {{ formatName(item.name) }}
            </template>

            <template #[`item.gst_percentage`]="{ item }">
              {{ formatGST(item) }}
            </template>

            <template #[`item.fee_schedule`]="{ item }">
              <v-btn small text color="primary" @click="viewFeeSchedules(item)">
                <v-icon small>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>

            <!-- Active Status Chip -->
            <template #[`item.active`]="{ item }">
              <v-chip
                small
                :color="item.active ? 'success' : 'error'"
                text-color="white"
                style="cursor: pointer"
                @click="confirmToggle(item)"
              >
                {{ item.active ? "Active" : "Inactive" }}
              </v-chip>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Form Dialog -->
    <v-dialog v-model="dialog" width="900" persistent scrollable>
      <formDialog
        v-if="dialog"
        @closeDialog="closeDialog"
        @create="createProgramAddon"
        @update="updateProgramAddon"
        :programAddon="programAddon"
        :edit="editMode"
      />
    </v-dialog>

    <!-- Description Preview Dialog -->
    <v-dialog v-model="previewDialog" max-width="800">
      <v-card>
        <v-card-title class="primary white--text">
          Description Preview
          <v-spacer></v-spacer>
          <v-btn icon dark @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="description-preview" v-html="selectedDescription"></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="previewDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Combo Addons Dialog -->
    <v-dialog v-model="previewAddons" max-width="800">
      <v-card>
        <v-card-title class="primary white--text">
          Combo Add-on Lists
          <v-spacer></v-spacer>
          <v-btn icon dark @click="previewAddons = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-list dense class="py-0">
            <v-list-item
              v-for="(addon, index) in selectedAddonsDetails"
              :key="index"
              dense
              class="px-0 py-0 min-height-item"
            >
              <v-list-item-icon class="mr-2 my-1 pt-2">
                <v-icon small color="primary">mdi-package-variant</v-icon>
              </v-list-item-icon>
              <v-list-item-content class="py-1">
                <v-list-item-title class="text-body-2">
                  {{ addon.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  GST:
                  {{
                    addon.is_gst_included
                      ? `${addon.gst_percentage}%`
                      : "Not included"
                  }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div
            v-if="selectedAddonsDetails.length === 0"
            class="text-center py-6"
          >
            <v-icon size="50" color="grey">mdi-package-variant-closed</v-icon>
            <p class="mt-2">No add-ons found in this combo</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Toggle Active/Inactive Confirmation Dialog -->
    <v-dialog v-model="toggleDialog" max-width="450" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          Confirmation
          <v-spacer></v-spacer>
          <v-btn icon dark @click="toggleDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6">
          <p class="text-body-1 mb-0">
            Are you sure you want to
            <strong>{{
              toggleTarget && toggleTarget.active ? "deactivate" : "activate"
            }}</strong>
            the add-on programme
            <strong
              >"{{ toggleTarget ? formatName(toggleTarget.name) : "" }}"</strong
            >?
          </p>
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer></v-spacer>
          <v-btn text @click="toggleDialog = false">Cancel</v-btn>
          <v-btn
            :color="toggleTarget && toggleTarget.active ? 'error' : 'success'"
            dark
            :loading="toggleLoading"
            @click="toggleActive"
          >
            {{
              toggleTarget && toggleTarget.active ? "Deactivate" : "Activate"
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import formDialog from "@/components/ProgramAddons/form.vue";
import searchBar from "@/components/searchBar.vue";
import { programAddonHeaders } from "@/constants/headers";

export default {
  name: "ProgramAddonsList",

  components: {
    formDialog,
    searchBar,
  },

  data() {
    return {
      dialog: false,
      editMode: false,
      programAddon: {},
      programAddons: [],
      search: "",
      headers: programAddonHeaders,
      previewDialog: false,
      previewAddons: false,
      selectedDescription: "",
      selectedAddons: [],
      selectedAddonsDetails: [],
      feeSchedules: [],
      statusFilter: "all",
      toggleDialog: false,
      toggleTarget: null,
      toggleLoading: false,
      loading: false,
    };
  },

  computed: {
    filteredAddons() {
      if (this.statusFilter === "active") {
        return this.programAddons.filter((a) => a.active === true);
      } else if (this.statusFilter === "inactive") {
        return this.programAddons.filter((a) => a.active === false);
      }
      return this.programAddons;
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Add-on Programmes" }]);
    this.initialise();
  },

  methods: {
    formatName(name) {
      if (!name) return "";
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    },

    openCreateDialog() {
      this.editMode = false;
      this.programAddon = {};
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.editMode = false;
      this.programAddon = {};
    },

    async initialise() {
      await this.getProgramAddons();
    },

    async getProgramAddons() {
      this.loading = true;
      try {
        const res = await this.$axios.get("program_addons");
        // Sort by ID descending (newest first)
        this.programAddons = (res.data.data || []).sort((a, b) => b.id - a.id);
      } catch (error) {
        console.error("Error fetching program addons:", error);
        this.$store.commit("showSnackbar", {
          message:
            error.response?.data?.message || "Error fetching program addons",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async createProgramAddon(data) {
      try {
        const res = await this.$axios.post("program_addons", {
          program_addons: data,
        });
        this.$store.commit("showSnackbar", {
          message: "Program addon created successfully",
          color: "success",
        });
        this.closeDialog();
        await this.getProgramAddons();
        return res;
      } catch (error) {
        console.error("Error creating program addon:", error);
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage || "Error creating program addon",
          color: "error",
        });
        throw error;
      }
    },

    async updateProgramAddon(data) {
      try {
        const res = await this.$axios.put(`program_addons/${data.id}`, {
          program_addons: data,
        });
        this.$store.commit("showSnackbar", {
          message: "Program addon updated successfully",
          color: "success",
        });
        this.closeDialog();
        await this.getProgramAddons();
        return res;
      } catch (error) {
        console.error("Error updating program addon:", error);
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage || "Error updating program addon",
          color: "error",
        });
        throw error;
      }
    },

    edit(item) {
      this.programAddon = { ...item };
      this.editMode = true;
      this.dialog = true;
    },

    viewFeeSchedules(item) {
      this.$router.push({
        path: `/program_addons_fee_schedule`,
        query: {
          program_addon_id: item.id,
          program_addon_name: item.name,
        },
      });
    },

    customFilter(value, search, item) {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      return (
        item.name?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.type?.toLowerCase().includes(searchLower)
      );
    },

    showDialog(item) {
      this.selectedDescription = item.description || "No description available";
      this.previewDialog = true;
    },

    showAddons(item) {
      let comboAddons = item.combo_addons;

      // Handle different formats of combo_addons
      if (!comboAddons) {
        this.selectedAddons = [];
      } else if (typeof comboAddons === "string") {
        try {
          this.selectedAddons = JSON.parse(comboAddons);
        } catch {
          this.selectedAddons = [];
        }
      } else if (
        typeof comboAddons === "object" &&
        !Array.isArray(comboAddons)
      ) {
        this.selectedAddons = Object.values(comboAddons);
      } else {
        this.selectedAddons = comboAddons;
      }

      // Filter to get details of selected add-ons
      this.selectedAddonsDetails = this.programAddons.filter(
        (addon) =>
          this.selectedAddons.includes(addon.id) && addon.id !== item.id,
      );

      this.previewAddons = true;
    },

    confirmToggle(item) {
      this.toggleTarget = item;
      this.toggleDialog = true;
    },

    async toggleActive() {
      if (!this.toggleTarget) return;

      this.toggleLoading = true;
      const updated = {
        ...this.toggleTarget,
        active: !this.toggleTarget.active,
      };

      try {
        await this.$axios.put(`program_addons/${updated.id}`, {
          program_addons: updated,
        });
        this.$store.commit("showSnackbar", {
          message: `Add-on programme ${
            updated.active ? "activated" : "deactivated"
          } successfully`,
          color: "success",
        });
        this.toggleDialog = false;
        this.toggleTarget = null;
        await this.getProgramAddons();
      } catch (error) {
        console.error("Error toggling add-on status:", error);
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage || "Error updating add-on status",
          color: "error",
        });
      } finally {
        this.toggleLoading = false;
      }
    },
    formatGST(item) {
      if (!item.gst_percentage) return "-";

      const roundedPercentage = Math.round(item.gst_percentage);
      const gstType = item.gst_type === "inclusive" ? "Inc" : "Excl";

      return `${roundedPercentage}% ${gstType}`;
    },
  },
};
</script>

<style scoped>
.description-preview {
  line-height: 1.6;
  font-size: 14px;
}

.description-preview >>> p {
  margin-bottom: 10px;
}

.description-preview >>> ul,
.description-preview >>> ol {
  margin-left: 20px;
  margin-bottom: 10px;
}

.description-preview >>> strong,
.description-preview >>> b {
  font-weight: bold;
}

.description-preview >>> em,
.description-preview >>> i {
  font-style: italic;
}

.description-preview >>> a {
  color: #4f4584;
  text-decoration: underline;
}

.description-preview >>> h1,
.description-preview >>> h2,
.description-preview >>> h3,
.description-preview >>> h4,
.description-preview >>> h5,
.description-preview >>> h6 {
  margin-top: 16px;
  margin-bottom: 12px;
}

/* Table row height */
.row-height-50 >>> tbody tr {
  height: 50px;
}

/* Min height for list items */
.min-height-item {
  min-height: auto !important;
}
</style>
